import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { LoadingController } from '@ionic/angular/standalone';
import { firstValueFrom } from 'rxjs';
import { Firestore, doc, getDoc, setDoc } from '@angular/fire/firestore';
import { Storage } from '@angular/fire/storage';
import { Database } from '@angular/fire/database';
import { AuthService } from 'src/app/auth.service';
import { MIGRATION_STEPS } from './migration-steps';

/**
 * Get completed migration phases from Firestore
 */
export async function getCompletedMigrations(
  firestore: Firestore,
  uid: string,
): Promise<Record<string, boolean>> {
  const userDocRef = doc(firestore, `users/${uid}`);
  const userDoc = await getDoc(userDocRef);

  if (!userDoc.exists()) {
    return {};
  }

  const data = userDoc.data();
  return (data?.['migration'] as Record<string, boolean>) ?? {};
}

/**
 * Mark a migration phase as completed in Firestore
 *
 * Uses setDoc with mergeFields to safely update only the specific migration flag.
 * This creates the user document if it doesn't exist (handling new users with no RTDB data)
 * and preserves all other fields including other migration flags.
 */
export async function markMigrationComplete(
  firestore: Firestore,
  uid: string,
  migrationId: string,
): Promise<void> {
  const userDocRef = doc(firestore, `users/${uid}`);
  const fieldPath = `migration.${migrationId}`;
  await setDoc(
    userDocRef,
    {
      migration: {
        [migrationId]: true,
      },
    },
    { mergeFields: [fieldPath] }
  );
}

/**
 * Migration Guard
 *
 * Orchestrates all migration phases in chronological order.
 * See migration-steps.ts for step definitions.
 *
 * Each phase reads from RTDB and writes to Firestore.
 * Tracks completion state in Firestore to skip already-completed phases.
 * This guard runs on authenticated route activation.
 */
export const migrationGuard: CanActivateFn = async () => {
  const loadingCtrl = inject(LoadingController);
  const authService = inject(AuthService);
  const firestore = inject(Firestore);
  const db = inject(Database);
  const storage = inject(Storage);

  const uid = await firstValueFrom(authService.uid$);
  const loaders: HTMLIonLoadingElement[] = [];

  try {
    // Get completed migrations from Firestore
    const completedMigrations = await getCompletedMigrations(firestore, uid);

    // Execute each migration step in order (skip if already completed)
    for (const step of MIGRATION_STEPS) {
      // Skip if this migration phase is already completed
      if (completedMigrations[step.id]) {
        console.log(`Skipping migration '${step.id}' - already completed`);
        continue;
      }

      const loader = await loadingCtrl.create({
        message: `${step.name}...`,
      });
      loaders.push(loader);

      for await (const progress of step.migrate(
        uid,
        db,
        firestore,
        storage,
      )) {
        if (progress.type === 'done') {
          loader.isOpen = false;
        } else if (progress.type === 'progress' && !loader.isOpen) {
          loader.isOpen = true;
        } else if (progress.type === 'progress') {
          const message = progress.message
            ? progress.message
            : `${step.name}... (${progress.current}/${progress.total})`;
          loader.message = message;
        }
      }

      // Mark this phase as completed in Firestore
      await markMigrationComplete(firestore, uid, step.id);
      console.log(`Migration '${step.id}' completed and saved to Firestore`);
    }

    return true;
  } finally {
    // Clean up all loaders
    for (const loader of loaders) {
      loader.remove();
    }
  }
};
